import {
	CodeBlock,
	Endpoint,
	Alert,
	Table,
	ParamList,
	Param,
	Step,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function GamesDice() {
	const multiplierData = [
		{ target: 1, probability: '1%', multiplier: '98.01x', condition: 'Under' },
		{ target: 5, probability: '5%', multiplier: '19.60x', condition: 'Under' },
		{ target: 10, probability: '10%', multiplier: '9.80x', condition: 'Under' },
		{ target: 25, probability: '25%', multiplier: '3.92x', condition: 'Under' },
		{ target: 50, probability: '50%', multiplier: '1.96x', condition: 'Under' },
		{ target: 75, probability: '75%', multiplier: '1.307x', condition: 'Under' },
		{ target: 90, probability: '90%', multiplier: '1.089x', condition: 'Under' },
		{ target: 95, probability: '95%', multiplier: '1.031x', condition: 'Under' },
		{ target: 99, probability: '99%', multiplier: '0.99x', condition: 'Under' },
	];

	const multiplierColumns = [
		{ key: 'target', header: 'Target' },
		{ key: 'probability', header: 'Win Probability' },
		{ key: 'multiplier', header: 'Multiplier' },
		{ key: 'condition', header: 'Condition' },
	];

	return (
		<div className={styles.docsPage}>
			<h1>Dice Game</h1>
			<p className={styles.lead}>
				A classic provably fair dice game where players predict whether a random
				roll will land above or below a chosen target number. Simple mechanics
				with flexible risk and reward options.
			</p>

			<Alert variant="info" title="House Edge">
				The Dice game has a fixed 1% house edge. This is factored into the
				payout multiplier calculation automatically.
			</Alert>

			<h2>Game Overview</h2>
			<p>
				The Dice game generates a random number between 0.00 and 99.99. Players
				choose a target number (1-98) and predict whether the roll will be{' '}
				<strong>over</strong> or <strong>under</strong> their target. The payout
				multiplier is calculated based on the probability of winning.
			</p>

			<h3>How It Works</h3>
			<ol>
				<li>Player selects a target number between 1 and 98</li>
				<li>
					Player chooses a condition: <code>over</code> or <code>under</code>
				</li>
				<li>Player places their bet amount</li>
				<li>The server generates a provably fair random roll (0.00 - 99.99)</li>
				<li>
					If the roll meets the condition, the player wins the bet amount
					multiplied by the payout multiplier
				</li>
			</ol>

			<h3>Win Conditions</h3>
			<ul>
				<li>
					<strong>Under:</strong> Player wins if the roll is less than the
					target number
				</li>
				<li>
					<strong>Over:</strong> Player wins if the roll is greater than the
					target number
				</li>
			</ul>

			<h2>Bet Parameters</h2>
			<ParamList>
				<Param name="amount" type="number" required>
					The bet amount in the player's currency. Must be positive and within
					the operator's configured min/max limits.
				</Param>
				<Param name="target" type="number" required>
					The target number for the dice roll. Must be an integer between 1 and
					98 inclusive. Lower targets with "under" condition have higher risk
					and reward.
				</Param>
				<Param name="condition" type="string" required>
					The win condition: either <code>"over"</code> or <code>"under"</code>.
					Determines whether the player wins when the roll is above or below the
					target.
				</Param>
				<Param name="currency" type="string">
					Optional currency code (e.g., "USD", "BTC"). Defaults to the session's
					primary currency.
				</Param>
			</ParamList>

			<h2>Payout Calculation</h2>
			<p>
				The payout multiplier is calculated based on the win probability, with
				the 1% house edge factored in:
			</p>

			<CodeBlock language="typescript" title="Payout Formula">
				{`// For "under" condition:
winProbability = target / 100
multiplier = (100 - houseEdge) / (target)
// Example: target = 50, houseEdge = 1%
// multiplier = 99 / 50 = 1.98x

// For "over" condition:
winProbability = (100 - target) / 100
multiplier = (100 - houseEdge) / (100 - target)
// Example: target = 50, houseEdge = 1%
// multiplier = 99 / 50 = 1.98x

// General formula:
multiplier = 99 / winProbabilityPercent
payout = betAmount * multiplier (if win)`}
			</CodeBlock>

			<h3>Multiplier Examples</h3>
			<Table columns={multiplierColumns} data={multiplierData} />

			<Alert variant="warning" title="Multiplier Range">
				Multipliers range from approximately <strong>1.01x</strong> (98% win
				chance) to <strong>98.01x</strong> (1% win chance). Extremely low or
				high targets are rejected to prevent edge cases.
			</Alert>

			<h2>API Endpoints</h2>

			<h3>Place Dice Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/games/dice/bet"
				description="Place a new dice bet and receive the result"
			/>

			<h4>Request Body</h4>
			<CodeBlock language="json" title="Request">
				{`{
  "sessionToken": "eyJhbGciOiJIUzI1NiIs...",
  "amount": 10.00,
  "target": 50,
  "condition": "under",
  "currency": "USD",
  "clientSeed": "my-custom-seed-123"
}`}
			</CodeBlock>

			<h4>Response</h4>
			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "betId": "bet_dice_abc123xyz",
    "game": "dice",
    "amount": 10.00,
    "currency": "USD",
    "target": 50,
    "condition": "under",
    "roll": 34.56,
    "multiplier": 1.98,
    "win": true,
    "payout": 19.80,
    "profit": 9.80,
    "balanceAfter": 109.80,
    "timestamp": "2024-01-15T12:30:45.123Z",
    "provablyFair": {
      "serverSeedHash": "a1b2c3d4e5f6...",
      "clientSeed": "my-custom-seed-123",
      "nonce": 42,
      "serverSeed": null
    }
  }
}`}
			</CodeBlock>

			<h3>Get Dice Game Info</h3>
			<Endpoint
				method="GET"
				path="/api/v1/games/dice/info"
				description="Get game configuration and limits"
			/>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "game": "dice",
    "name": "Dice",
    "description": "Classic dice roll prediction game",
    "houseEdge": 1.0,
    "minBet": 0.10,
    "maxBet": 10000.00,
    "minTarget": 1,
    "maxTarget": 98,
    "conditions": ["over", "under"],
    "minMultiplier": 1.0102,
    "maxMultiplier": 98.0196
  }
}`}
			</CodeBlock>

			<h2>Complete Integration Example</h2>
			<p>
				Here is a complete TypeScript example showing how to integrate the Dice
				game into your application:
			</p>

			<CodeBlock language="typescript" title="dice-integration.ts" showLineNumbers>
				{`import { createHmac } from 'crypto';

interface DiceBetParams {
  sessionToken: string;
  amount: number;
  target: number;
  condition: 'over' | 'under';
  clientSeed?: string;
}

interface DiceBetResult {
  betId: string;
  roll: number;
  win: boolean;
  payout: number;
  multiplier: number;
  provablyFair: {
    serverSeedHash: string;
    clientSeed: string;
    nonce: number;
  };
}

const API_BASE = 'https://api.ringfort.studio/v1';

// Calculate expected multiplier before placing bet
function calculateMultiplier(target: number, condition: 'over' | 'under'): number {
  const houseEdge = 1; // 1%
  const winChance = condition === 'under' ? target : 100 - target;
  return (100 - houseEdge) / winChance;
}

// Place a dice bet
async function placeDiceBet(params: DiceBetParams): Promise<DiceBetResult> {
  const clientSeed = params.clientSeed || generateClientSeed();

  const response = await fetch(\`\${API_BASE}/games/dice/bet\`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionToken: params.sessionToken,
      amount: params.amount,
      target: params.target,
      condition: params.condition,
      clientSeed,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Bet placement failed');
  }

  const result = await response.json();
  return result.data;
}

// Generate a random client seed
function generateClientSeed(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let seed = '';
  for (let i = 0; i < 16; i++) {
    seed += chars[Math.floor(Math.random() * chars.length)];
  }
  return seed;
}

// Example usage
async function main() {
  const sessionToken = 'your-session-token';

  // Calculate potential payout before betting
  const target = 33;
  const condition = 'under';
  const multiplier = calculateMultiplier(target, condition);
  console.log(\`Betting under \${target} gives \${multiplier.toFixed(2)}x multiplier\`);

  // Place the bet
  try {
    const result = await placeDiceBet({
      sessionToken,
      amount: 10.00,
      target,
      condition,
    });

    console.log(\`Roll: \${result.roll}\`);
    console.log(\`Win: \${result.win}\`);
    console.log(\`Payout: \${result.payout}\`);
    console.log(\`Bet ID: \${result.betId}\`);
  } catch (error) {
    console.error('Bet failed:', error);
  }
}

main();`}
			</CodeBlock>

			<h2>Provably Fair Verification</h2>
			<p>
				Every Dice game result can be independently verified using our provably
				fair system. The outcome is determined by combining the server seed,
				client seed, and nonce.
			</p>

			<Step number={1} title="Before Placing Bet">
				<p>
					Before each bet, you receive a <code>serverSeedHash</code> which is
					the SHA-256 hash of the server seed. This commits the server to a
					specific seed without revealing it.
				</p>
			</Step>

			<Step number={2} title="Provide Your Client Seed">
				<p>
					You can provide your own <code>clientSeed</code> or let the system
					generate one. This ensures you have input into the random generation.
				</p>
			</Step>

			<Step number={3} title="Game Generates Result">
				<p>
					The server combines the server seed, client seed, and nonce to
					generate the roll using HMAC-SHA256:
				</p>
				<CodeBlock language="typescript">
					{`const combined = \`\${serverSeed}:\${clientSeed}:\${nonce}\`;
const hash = createHmac('sha256', serverSeed)
  .update(\`\${clientSeed}:\${nonce}\`)
  .digest('hex');

// Convert first 8 characters to roll (0.00 - 99.99)
const decimal = parseInt(hash.substring(0, 8), 16);
const roll = (decimal % 10000) / 100;`}
				</CodeBlock>
			</Step>

			<Step number={4} title="Verify After Rotation">
				<p>
					When you rotate your server seed (or it auto-rotates), you receive the
					previous <code>serverSeed</code>. You can then:
				</p>
				<ol>
					<li>
						Hash the revealed server seed and verify it matches the{' '}
						<code>serverSeedHash</code>
					</li>
					<li>
						Recalculate the roll using the formula above to confirm the result
					</li>
				</ol>
			</Step>

			<CodeBlock language="typescript" title="Verification Example">
				{`import { createHash, createHmac } from 'crypto';

function verifyDiceRoll(
  serverSeed: string,
  serverSeedHash: string,
  clientSeed: string,
  nonce: number,
  expectedRoll: number
): boolean {
  // Step 1: Verify server seed hash
  const computedHash = createHash('sha256')
    .update(serverSeed)
    .digest('hex');

  if (computedHash !== serverSeedHash) {
    console.error('Server seed hash mismatch!');
    return false;
  }

  // Step 2: Recalculate the roll
  const hmac = createHmac('sha256', serverSeed)
    .update(\`\${clientSeed}:\${nonce}\`)
    .digest('hex');

  const decimal = parseInt(hmac.substring(0, 8), 16);
  const calculatedRoll = (decimal % 10000) / 100;

  // Step 3: Compare with expected
  if (Math.abs(calculatedRoll - expectedRoll) > 0.001) {
    console.error(\`Roll mismatch! Expected \${expectedRoll}, got \${calculatedRoll}\`);
    return false;
  }

  console.log('Verification successful!');
  return true;
}`}
			</CodeBlock>

			<h2>Strategy Tips</h2>
			<p>
				While the Dice game is purely based on chance, understanding the
				mathematics can help you make informed decisions:
			</p>

			<h3>Risk vs Reward</h3>
			<ul>
				<li>
					<strong>Low Risk (high target under or low target over):</strong>{' '}
					Higher win probability but lower multipliers. Good for steady
					gameplay.
				</li>
				<li>
					<strong>Medium Risk (target around 33-66):</strong> Balanced approach
					with moderate multipliers (1.5x - 3x).
				</li>
				<li>
					<strong>High Risk (low target under or high target over):</strong> Low
					win probability but high multipliers. More volatile gameplay.
				</li>
			</ul>

			<h3>Bankroll Management</h3>
			<ul>
				<li>Set a budget before playing and stick to it</li>
				<li>Consider using smaller bet amounts for high-risk targets</li>
				<li>The house edge is the same regardless of target choice</li>
				<li>
					Expected value (EV) is always -1% of your bet amount over time
				</li>
			</ul>

			<Alert variant="info" title="Fair Gameplay">
				All outcomes are determined by the provably fair algorithm. Past results
				do not influence future rolls - each roll is independent with the same
				probabilities regardless of previous outcomes.
			</Alert>

			<h2>Error Handling</h2>
			<p>Common errors you may encounter when integrating the Dice game:</p>

			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'message', header: 'Message' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{
						code: 'INVALID_TARGET',
						message: 'Target must be between 1 and 98',
						solution: 'Ensure target is an integer in the valid range',
					},
					{
						code: 'INVALID_CONDITION',
						message: 'Condition must be "over" or "under"',
						solution: 'Use exactly "over" or "under" as the condition',
					},
					{
						code: 'INSUFFICIENT_BALANCE',
						message: 'Insufficient balance for bet',
						solution: 'Check player balance before placing bet',
					},
					{
						code: 'BET_BELOW_MINIMUM',
						message: 'Bet amount below minimum',
						solution: 'Increase bet to meet minimum requirement',
					},
					{
						code: 'BET_ABOVE_MAXIMUM',
						message: 'Bet amount exceeds maximum',
						solution: 'Reduce bet to stay within limits',
					},
					{
						code: 'SESSION_EXPIRED',
						message: 'Session token has expired',
						solution: 'Request a new session token from the operator',
					},
				]}
			/>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/provably-fair">
							Learn more about Provably Fair verification
						</a>
					</li>
					<li>
						<a href="/docs/webhooks">Set up webhooks for bet notifications</a>
					</li>
					<li>
						<a href="/docs/games-plinko">Explore the Plinko game</a>
					</li>
				</ul>
			</div>
		</div>
	);
}
